import { useState, useEffect, useCallback } from 'react';

const canNativeFullscreen = () => {
  return !!(
    document.fullscreenEnabled ||
    document.webkitFullscreenEnabled ||
    document.documentElement.requestFullscreen ||
    document.documentElement.webkitRequestFullscreen
  );
};

export const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      const native = !!(document.fullscreenElement || document.webkitFullscreenElement);
      const soft = document.body.classList.contains('soft-fullscreen');
      setIsFullscreen(native || soft);
    };
    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const isCurrentlyNative = !!(document.fullscreenElement || document.webkitFullscreenElement);
    const isCurrentlySoft = document.body.classList.contains('soft-fullscreen');

    if (isCurrentlyNative) {
      try {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      } catch (e) {}
      return;
    }

    if (isCurrentlySoft) {
      document.body.classList.remove('soft-fullscreen');
      setIsFullscreen(false);
      return;
    }

    // Try native first
    let nativeWorked = false;
    if (canNativeFullscreen()) {
      try {
        const el = document.documentElement;
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        }
        // Verify native actually activated (some tablets resolve but do nothing)
        await new Promise(r => setTimeout(r, 300));
        nativeWorked = !!(document.fullscreenElement || document.webkitFullscreenElement);
      } catch (e) {
        nativeWorked = false;
      }
    }

    // Fallback: soft fullscreen (hides nav bar via CSS class)
    if (!nativeWorked) {
      document.body.classList.add('soft-fullscreen');
      setIsFullscreen(true);
    }
  }, []);

  return { isFullscreen, toggleFullscreen };
};
